import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Loading, Modal } from '@evertrue/et-components';
import { isBulkMessageType } from '../../utils/cadence-enums';

const BULK_MESSAGE_OPTIONS = [
  { key: 'messageContent', label: 'Message copy (subject, body, sender info)', defaultChecked: true },
  { key: 'recipientSelection', label: 'Recipient selection', defaultChecked: true },
  { key: 'successMetrics', label: 'Success metric(s)', defaultChecked: true },
  { key: 'linkedProposals', label: 'Linked proposals', defaultChecked: true },
  { key: 'shareSettings', label: 'Share settings', defaultChecked: false },
  { key: 'cadenceShell', label: 'Cadence Blueprint', defaultChecked: false },
  { key: 'tags', label: 'Tags', defaultChecked: true },
];

const CADENCE_OPTIONS = [
  {
    key: 'stepSetup',
    label: 'Step setup',
    description: 'Including each step (plus templates & automation settings), wait times, and conditions',
    defaultChecked: true,
  },
  { key: 'recipientSelection', label: 'Recipient selection', defaultChecked: true },
  { key: 'successMetrics', label: 'Success metric(s)', defaultChecked: true },
  { key: 'exitEvents', label: 'Exit event(s)', defaultChecked: true },
  { key: 'linkedProposals', label: 'Linked proposals', defaultChecked: true },
  { key: 'shareSettings', label: 'Share settings', defaultChecked: false },
  { key: 'cadenceShell', label: 'Cadence Blueprint', defaultChecked: false },
  { key: 'tags', label: 'Tags', defaultChecked: true },
];

const TEMPLATE_OPTIONS = [
  {
    key: 'stepSetup',
    label: 'Message copy / Step setup',
    description: 'If multi-step, includes each step (plus templates & automation settings), wait times, and conditions',
    defaultChecked: true,
  },
  { key: 'successMetrics', label: 'Success metric(s)', defaultChecked: true },
  { key: 'exitEvents', label: 'Exit event(s)', defaultChecked: true },
  { key: 'linkedProposals', label: 'Linked proposals', defaultChecked: true },
  { key: 'shareSettings', label: 'Share settings', defaultChecked: false },
  { key: 'tags', label: 'Tags', defaultChecked: true },
];

// Blueprint options — audience/recipients shown but disabled (never makes sense for a blueprint)
const BLUEPRINT_OPTIONS = [
  {
    key: 'stepSetup',
    label: 'Step setup',
    description: 'Each step (templates, automation settings), wait times, and conditions',
    defaultChecked: true,
    alwaysOn: true,
  },
  {
    key: 'recipientSelection',
    label: 'Recipient selection',
    defaultChecked: false,
    disabled: true,
    disabledReason: 'Blueprints do not include recipients',
  },
  { key: 'successMetrics', label: 'Success metric(s)', defaultChecked: true },
  { key: 'exitEvents', label: 'Exit event(s)', defaultChecked: true },
  { key: 'linkedProposals', label: 'Linked proposals', defaultChecked: true },
  { key: 'shareSettings', label: 'Share settings', defaultChecked: true },
  { key: 'tags', label: 'Tags', defaultChecked: false },
];

/**
 * Modal for duplicating a cadence, bulk message, or template, and for saving as a blueprint.
 * Pass entityType='blueprint' to use the "Save as Blueprint" flow.
 */
const DuplicateCadenceModal = ({ isOpen, onClose, onDuplicate, entityName, entityType, sourceArchived }) => {
  const [name, setName] = useState('');
  const [checkedOptions, setCheckedOptions] = useState({});
  const [busy, setBusy] = useState(false);

  const isBulkMessage = isBulkMessageType(entityType);
  const isTemplate = entityType === 'template';
  const isBlueprint = entityType === 'blueprint';

  const baseOptions = isBlueprint
    ? BLUEPRINT_OPTIONS
    : isTemplate
      ? TEMPLATE_OPTIONS
      : isBulkMessage
        ? BULK_MESSAGE_OPTIONS
        : CADENCE_OPTIONS;

  // Archived cadences can't restore recipients — member state, tasks, and journey progress are lost
  const options = sourceArchived
    ? baseOptions.map((opt) =>
        opt.key === 'recipientSelection'
          ? {
              ...opt,
              defaultChecked: false,
              disabled: true,
              disabledReason: 'Recipients cannot be restored from archived cadences',
            }
          : opt
      )
    : baseOptions;

  useEffect(() => {
    if (isOpen) {
      const suffix = isBlueprint ? '(Blueprint)' : isTemplate ? '(Copied From Blueprint)' : '(Copy)';
      setName(`${entityName || 'Untitled'} ${suffix}`);
      const defaults = {};
      options.forEach((opt) => {
        defaults[opt.key] = opt.defaultChecked;
      });
      setCheckedOptions(defaults);
      setBusy(false);
    }
  }, [isOpen, entityName, isTemplate, isBlueprint, sourceArchived, options]);

  const handleToggle = (key) => {
    setCheckedOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleConfirm = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onDuplicate(name.trim(), checkedOptions);
      onClose();
    } catch {
      setBusy(false);
    }
  };

  const typeLabel = isBlueprint
    ? 'Cadence Blueprint'
    : isTemplate
      ? 'Cadence Blueprint'
      : isBulkMessage
        ? 'Bulk Message'
        : 'Cadence';

  const title = isBlueprint
    ? 'Save as Blueprint'
    : sourceArchived
      ? `Duplicate ${typeLabel} as Draft`
      : `Duplicate ${typeLabel}`;
  const confirmLabel = isBlueprint ? 'Save as Blueprint' : sourceArchived ? 'Duplicate as Draft' : 'Duplicate';
  const namefieldLabel = isBlueprint ? 'BLUEPRINT NAME' : `NEW ${typeLabel.toUpperCase()} NAME`;

  return (
    <Modal isOpen={isOpen} closeModal={onClose}>
      <div className="duplicate-cadence-modal">
        <div className="duplicate-cadence-modal--header">
          <h3 className="duplicate-cadence-modal--title">{title}</h3>
          <button type="button" className="duplicate-cadence-modal--close" onClick={onClose}>
            <Icon icon="close" />
          </button>
        </div>

        <div className="duplicate-cadence-modal--body">
          {isBlueprint && (
            <p className="duplicate-cadence-modal--blueprint-note">
              A blueprint captures your cadence structure for reuse. Audience, recipients, and sharing settings are not
              included.
            </p>
          )}
          {sourceArchived && !isBlueprint && (
            <p className="duplicate-cadence-modal--blueprint-note">
              This will create a new draft copy of the archived cadence. Recipients cannot be included because member
              progress, tasks, and journey state are not preserved after archiving.
            </p>
          )}

          <label className="duplicate-cadence-modal--field-label">{namefieldLabel}</label>
          <input
            type="text"
            className="duplicate-cadence-modal--name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) handleConfirm();
            }}
            autoFocus
          />

          <label className="duplicate-cadence-modal--field-label duplicate-cadence-modal--include-label">
            WHAT TO INCLUDE
          </label>
          <div className="duplicate-cadence-modal--options">
            {options.map((opt) => {
              const isLocked = !!opt.alwaysOn;
              const isDisabled = !!opt.disabled;
              const isInteractive = !isLocked && !isDisabled;
              return (
                <label
                  key={opt.key}
                  className={`duplicate-cadence-modal--option ${isLocked ? 'is-locked' : ''} ${isDisabled ? 'is-disabled' : ''}`}
                >
                  <span
                    className={`duplicate-cadence-modal--checkbox ${checkedOptions[opt.key] ? 'is-checked' : ''} ${!isInteractive ? 'is-disabled' : ''}`}
                    onClick={() => isInteractive && handleToggle(opt.key)}
                    role="checkbox"
                    aria-checked={!!checkedOptions[opt.key]}
                    aria-disabled={!isInteractive}
                    tabIndex={isInteractive ? 0 : -1}
                    onKeyDown={(e) => {
                      if (isInteractive && (e.key === ' ' || e.key === 'Enter')) {
                        e.preventDefault();
                        handleToggle(opt.key);
                      }
                    }}
                  >
                    {checkedOptions[opt.key] && <Icon icon="check" />}
                  </span>
                  <div className="duplicate-cadence-modal--option-text">
                    <span className="duplicate-cadence-modal--option-label">
                      {opt.label}
                      {isLocked && <span className="duplicate-cadence-modal--always-on"> (always included)</span>}
                      {isDisabled && opt.disabledReason && (
                        <span className="duplicate-cadence-modal--disabled-reason"> ({opt.disabledReason})</span>
                      )}
                    </span>
                    {opt.description && <span className="duplicate-cadence-modal--option-desc">{opt.description}</span>}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="duplicate-cadence-modal--footer">
          <Button type="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="action" onClick={handleConfirm} disabled={busy || !name.trim()}>
            {busy ? <Loading size="small" /> : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

DuplicateCadenceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  entityName: PropTypes.string,
  entityType: PropTypes.string,
  sourceArchived: PropTypes.bool,
};

export default DuplicateCadenceModal;
